import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { FooterButtons, ResizableContainer, MailsButtons, EmailButton } from "./styles";
import { Button, Collapse, Dropdown, Menu, message } from "antd";
import { sanitizeHtml } from "../../../utils/helpers/sanitizeHtml";
import { MoreOutlined, CopyOutlined } from "@ant-design/icons";
import { ActivationStatus } from "../../../types";
import SkeletonButton from "antd/es/skeleton/Button";
import StyledModal from "../../../components/CustomModal";
import { AddButton } from "../../Category/styles";
import { FormWrapper } from "../../../components/styles";
import { Form, Input, Skeleton, Tooltip } from "antd";
import { useAppDispatch, useAppSelector } from "../../../utils/hooks/redux";
import { selectEmailTemplate } from "../../../redux/features/emailTemplate/emailTemplateSlice";
import { fetchEmailTemplates, postEmailTemplates } from "../../../redux/features/emailTemplate/emailTemplateAsyncThunk";
import { EmailTemplate } from "../../../types/EmailTemplateTypes";
import { RequestResults } from "../../../redux/common/RequestStatus";
import { getEmailContext } from "../../../redux/features/report/reportAsyncThunk";
import ReactQuill from "react-quill";
import 'react-quill/dist/quill.snow.css';
import { MailOutlined } from "@ant-design/icons";
import { replaceDynamicVariables, variableCategories } from "../../EmailTemplates/EmailTemplateConst";
import { VariableTag } from "../../EmailTemplates/styles";
import { selectUserRole } from "../../../redux/features/auth/selectors";
import { checkHasPermission } from "../../../utils/helpers/checkHasPermission";
import { permissions } from "../../../utils/constants/permissions";

interface ReportDetailsFooterProps {
}

const ReportDetailsFooter: React.FC<ReportDetailsFooterProps> = () => {
  const { report } = useAppSelector((state) => state.report);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isBodyLoading, setIsBodyLoading] = useState(false);
  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState<EmailTemplate | null>(null);
  const [isPostMailLoading, setIsPostMailLoading] = useState<boolean>(false);
  const [previewBody, setPreviewBody] = useState<string>("");
  const [form] = Form.useForm();

  const dispatch = useAppDispatch();
  const { data: emailTemplateData, status: emailStatus } = useAppSelector(selectEmailTemplate);
  const userRole = useAppSelector(selectUserRole);

  const hasPermission = useCallback(
    (permissionKey: string) => checkHasPermission(userRole, permissionKey),
    [userRole]
  );
  const isSendPermission = useMemo(() => hasPermission(permissions.IsSendEmailTemplate), [hasPermission]);

  const copyVariable = (variable: string) => {
    navigator.clipboard.writeText(`{{${variable}}}`);
    message.success({ content: `Copied {{${variable}}}`, duration: 1.5 });
  };

  const CopyableTag = ({ name }: { name: string }) => (
    <Tooltip title="Click to copy">
      <VariableTag color="processing" style={{ cursor: "pointer", userSelect: "none" }} onClick={() => copyVariable(name)}>
        <CopyOutlined style={{ fontSize: 10, marginRight: 3 }} />{`{{${name}}}`}
      </VariableTag>
    </Tooltip>
  );

  useEffect(() => {
    if (emailStatus === RequestResults.idle) {
      dispatch(fetchEmailTemplates({ status: ActivationStatus.Active }));
    }
  }, [dispatch, emailStatus]);

  const handleReportCancel = () => {
    setIsReportModalOpen(false);
    setSelectedEmailTemplate(null);
    setIsPostMailLoading(false);
    setPreviewBody("");
    form.resetFields();
  };

  const handleFormSubmit = async (values: any) => {
    setIsPostMailLoading(true);
    try {
      const recipientEmail = selectedEmailTemplate?.isNotToGuest
        ? values.recipientEmail
        : (report?.user?.email || report?.user?.email2);
      const { recipientEmail: _, ...restValues } = values;
      const postEmailData = {
        ...restValues,
        email: recipientEmail,
        lostId: report?.id,
        templateId: selectedEmailTemplate?.id,
      };
      await dispatch(postEmailTemplates(postEmailData));
      handleReportCancel();
    } catch (error) {
      console.error("Error during form submission:", error);
      setIsPostMailLoading(false);
    }
  };

  const handleFormError = (errorInfo: any) => {
    console.error("Form submission failed with errors:", errorInfo);
  };

  const setEmailTemplate = async (emailTemplate: EmailTemplate) => {
    let emailBody = emailTemplate.body || "";
    let subject = emailTemplate.subject || "";
    let signature = emailTemplate.signature || "";

    if (emailTemplate.id && report?.id) {
      try {
        setIsBodyLoading(true);
        const response = await dispatch(getEmailContext({
          templateId: emailTemplate.id,
          lostId: report?.id,
        })).unwrap();

        emailBody = replaceDynamicVariables(emailBody, response);
        subject = replaceDynamicVariables(subject, response);
        signature = replaceDynamicVariables(signature, response);
        setPreviewBody(emailBody);

        form.setFieldsValue({
          subject: subject,
          body: emailBody,
          signature: signature
        });
      } catch (error) {
        console.error("Error during email context generation:", error);
      } finally {
        setIsBodyLoading(false);
      }
    }
  };

  useEffect(() => {
    if (selectedEmailTemplate) {
      setEmailTemplate(selectedEmailTemplate);
    }
  }, [selectedEmailTemplate]);


  return (
    <>
      {report && (
        <div style={{ display: 'flex', alignItems: 'center', padding: '8px 16px', borderBottom: '1px solid #f0f0f0' }}>
          {emailStatus === RequestResults.pending ? (
            <SkeletonButton />
          ) : (
            <>
              <ResponsiveButtonMenu
                items={emailTemplateData || []}
                onItemClick={(item) => {
                  setIsReportModalOpen(true);
                  setSelectedEmailTemplate(item);
                }}
                disabled={false}
              />
            </>
          )}
        </div>
      )}

      {selectedEmailTemplate && isReportModalOpen &&
        <StyledModal
          draggable={true}
          title={selectedEmailTemplate?.longTitle}
          width={800}
          centered
          open={isReportModalOpen}
          onCancel={handleReportCancel}
          footer={
            <FooterButtons>
              <AddButton disabled={isPostMailLoading} onClick={handleReportCancel}>Cancel</AddButton>
              {isSendPermission && <AddButton loading={isPostMailLoading} onClick={() => form.submit()} type="primary">
                Send
              </AddButton>}
            </FooterButtons>
          }
        >
          <FormWrapper
            disabled={isPostMailLoading}
            form={form}
            name="ReportSubmit"
            layout="vertical"
            onFinish={handleFormSubmit}
            onFinishFailed={handleFormError}
          >
            {selectedEmailTemplate?.isNotToGuest ? (
              <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 6, padding: "8px 12px", marginBottom: 12, fontSize: 12, color: "#9a3412" }}>
                <strong>Internal Only</strong> — This template is marked "Not to Guest". Email will NOT be sent to the guest.
              </div>
            ) : (
              <div style={{ background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 6, padding: "8px 12px", marginBottom: 12, fontSize: 12, color: "#0369a1" }}>
                <MailOutlined style={{ marginRight: 4 }} /> Sending to: <strong>{report?.user?.email || report?.user?.email2 || "N/A"}</strong>
              </div>
            )}

            {selectedEmailTemplate?.isNotToGuest && (
              <Form.Item
                name="recipientEmail"
                label="Recipient Email"
                rules={[
                  { required: true, message: "Please enter recipient email!" },
                  { type: "email", message: "Please enter a valid email!" },
                ]}
              >
                <Input placeholder="Enter recipient email address" size="large" />
              </Form.Item>
            )}

            <Form.Item
              name="subject"
              label="Subject"
              rules={[{ required: true, message: "Please enter your Subject!" }]}
            >
              <Input placeholder="Subject" size="large" />
            </Form.Item>

            {isBodyLoading ? (
              <Skeleton active />
            ) : (
              <Form.Item
                name="body"
                label="Message"
                rules={[
                  { required: true, message: "" },
                  {
                    validator: (_, value) => {
                      const textContent = value?.replace(/<[^>]*>/g, '').trim();
                      if (!textContent) {
                        return Promise.reject(new Error('Please enter your Message!'));
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
                validateTrigger={['onChange', 'onBlur']}
              >
                <ResizableContainer>
                  {React.createElement(ReactQuill as any, {
                    theme: "snow",
                    onChange: (content: string) => {
                      form.setFieldsValue({ body: content });
                      setPreviewBody(content);
                      form.validateFields(['body']);
                    },
                    onBlur: () => {
                      form.validateFields(['body']);
                    },
                    value: form.getFieldValue("body") || ""
                  })}
                </ResizableContainer>
              </Form.Item>
            )}

            <div style={{ marginTop: 4, marginBottom: 12 }}>
              <Collapse size="small" items={[
                ...variableCategories.map((cat, i) => ({
                  key: String(i),
                  label: <span style={{ fontSize: 12, fontWeight: 600 }}>{cat.label}</span>,
                  children: (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                      {cat.vars.map((v) => <CopyableTag key={v} name={v} />)}
                    </div>
                  ),
                })),
                {
                  key: "qa",
                  label: <span style={{ fontSize: 12, fontWeight: 600 }}>Question Answers <span style={{ color: "#6b7280", fontWeight: 400 }}>(use exact question field name)</span></span>,
                  children: (
                    <div>
                      <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>
                        Use <code style={{ background: "#f3f4f6", padding: "1px 4px", borderRadius: 3 }}>{`{{Q:Question Name}}`}</code> — replace with your actual question field name.
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                        <CopyableTag name="Q:Flight Number" />
                        <CopyableTag name="Q:Airline" />
                        <CopyableTag name="Q:Terminal" />
                      </div>
                    </div>
                  ),
                },
              ]} />
            </div>

            <Form.Item
              label="Signature"
              name="signature"
              rules={[{ required: true, message: "Please enter your Signature!" }]}
            >
              <Input.TextArea rows={3} placeholder="Signature" />
            </Form.Item>

            {previewBody && (
              <div style={{ marginTop: "1.5rem" }}>
                <h4 style={{ marginBottom: "0.5rem", fontWeight: 400 }}><MailOutlined color="#144076" /> Email Preview</h4>
                <div
                  style={{
                    border: "1px solid #d9d9d9",
                    borderRadius: "4px",
                    padding: "12px",
                    backgroundColor: "#fafafa",
                    maxHeight: "250px",
                    overflowY: "auto",
                  }}
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(previewBody) }}
                />
              </div>
            )}

          </FormWrapper>
        </StyledModal>
      }
    </>
  );
};

// Responsive Button Menu component that shows buttons inline or in a dropdown based on available space
interface ResponsiveButtonMenuProps {
  items: EmailTemplate[];
  onItemClick: (item: EmailTemplate) => void;
  disabled?: boolean;
}

const ResponsiveButtonMenu: React.FC<ResponsiveButtonMenuProps> = ({ items, onItemClick, disabled }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [visibleButtons, setVisibleButtons] = useState<EmailTemplate[]>([]);
  const [dropdownItems, setDropdownItems] = useState<EmailTemplate[]>([]);

  useEffect(() => {
    const handleResize = () => {
      if (!containerRef.current || items.length === 0) return;

      const containerWidth = containerRef.current.offsetWidth;
      const buttonWidth = 120; // Approximate width of each button with margin
      const dropdownWidth = 50; // Width of the dropdown button
      const availableWidth = containerWidth - dropdownWidth;

      const maxVisibleButtons = Math.max(1, Math.floor(availableWidth / buttonWidth));

      if (items.length <= maxVisibleButtons) {
        // All buttons fit, no need for dropdown
        setVisibleButtons(items);
        setDropdownItems([]);
        setShowDropdown(false);
      } else {
        // Some buttons need to go in dropdown
        setVisibleButtons(items.slice(0, maxVisibleButtons - 1)); // -1 to make room for dropdown
        setDropdownItems(items.slice(maxVisibleButtons - 1));
        setShowDropdown(true);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [items]);

  const dropdownMenu = (
    <Menu>
      {dropdownItems.map(item => (
        <Menu.Item key={item.id} onClick={() => onItemClick(item)}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div
              style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                background: item.color,
                marginRight: '8px'
              }}
            />
            {item.shortTitle}
          </div>
        </Menu.Item>
      ))}
    </Menu>
  );

  return (
    <div ref={containerRef} style={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'flex-end' }}>
      <MailsButtons style={{ padding: visibleButtons.length > 0 ? "8px" : "4px" }}>
        {
          visibleButtons.length > 0 &&
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <mask id="mask0_4510_1675" maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
              <rect width="24" height="24" fill="#D9D9D9" />
            </mask>
            <g mask="url(#mask0_4510_1675)">
              <path d="M7 17C6.45 17 5.97917 16.8042 5.5875 16.4125C5.19583 16.0208 5 15.55 5 15V5C5 4.45 5.19583 3.97917 5.5875 3.5875C5.97917 3.19583 6.45 3 7 3H21C21.55 3 22.0208 3.19583 22.4125 3.5875C22.8042 3.97917 23 4.45 23 5V15C23 15.55 22.8042 16.0208 22.4125 16.4125C22.0208 16.8042 21.55 17 21 17H7ZM14 12.3L7 7.425V15H21V7.425L14 12.3ZM14 9.85L21 5H7L14 9.85ZM3 21C2.45 21 1.97917 20.8042 1.5875 20.4125C1.19583 20.0208 1 19.55 1 19V6.5H3V19H19.5V21H3Z" fill="#2F54EB" />
            </g>
          </svg>
        }
        {visibleButtons.map(item => (
          <Tooltip key={item.id} placement="top" title={item.longTitle}>
            <EmailButton
              color={item.color}
              onClick={() => onItemClick(item)}
              disabled={disabled}
            >
              {item.shortTitle}
            </EmailButton>
          </Tooltip>
        ))}

        {showDropdown && (
          <Dropdown overlay={dropdownMenu} trigger={['click']} placement="bottomRight">
            <Button
              icon={
                visibleButtons.length > 0 ? <MoreOutlined /> :
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <mask id="mask0_4510_1675" maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
                      <rect width="24" height="24" fill="#D9D9D9" />
                    </mask>
                    <g mask="url(#mask0_4510_1675)">
                      <path d="M7 17C6.45 17 5.97917 16.8042 5.5875 16.4125C5.19583 16.0208 5 15.55 5 15V5C5 4.45 5.19583 3.97917 5.5875 3.5875C5.97917 3.19583 6.45 3 7 3H21C21.55 3 22.0208 3.19583 22.4125 3.5875C22.8042 3.97917 23 4.45 23 5V15C23 15.55 22.8042 16.0208 22.4125 16.4125C22.0208 16.8042 21.55 17 21 17H7ZM14 12.3L7 7.425V15H21V7.425L14 12.3ZM14 9.85L21 5H7L14 9.85ZM3 21C2.45 21 1.97917 20.8042 1.5875 20.4125C1.19583 20.0208 1 19.55 1 19V6.5H3V19H19.5V21H3Z" fill="#2F54EB" />
                    </g>
                  </svg>
              }
              style={{
                borderRadius: '6px',
                flexShrink: 0,
              }}
              disabled={disabled}
            />
          </Dropdown>
        )}
      </MailsButtons>
    </div>
  );
};

export default ReportDetailsFooter;
